import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Typography, Grid, TextField, Box, MenuItem, IconButton, Snackbar, Modal, SpeedDial, Alert, SpeedDialAction, SpeedDialIcon, Tooltip, LinearProgress, Button, Select, FormControl, Pagination, Checkbox, FormControlLabel, Menu, Paper, Stack, Divider } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { CircleFlag } from 'react-circle-flags';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { jwtDecode } from "jwt-decode";
import AddCommentIcon from '@mui/icons-material/AddComment';
import TranslateIcon from '@mui/icons-material/Translate';
import HistoryIcon from '@mui/icons-material/History';
import AnnotationPanel from './AnnotationPanel';
import NoteIcon from '@mui/icons-material/Note';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import isoLanguages from './isoLanguages';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloseIcon from '@mui/icons-material/Close';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import SpellcheckEditor from './SpellcheckEditor';
import TagTextField, { stringToSlate, slateToString } from './TagTextField';
import TranslationRow from './TranslationRow';
import TranslationHeader from './TranslationHeader';
import SmartSuggestions from './SmartSuggestions';
import { languageToolLocales } from './languageToolLocales';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileIcon, defaultStyles } from 'react-file-icon';
import FolderIcon from '@mui/icons-material/Folder';
import { clearTranslationDrafts } from '../utils/drafts';
import { buildTagHtml } from '../utils/tagDisplay';
import { findSimilarSegments } from '../utils/similarity';
import ContextSyncAlert from './ContextSyncAlert';
import ContextSyncModal from './ContextSyncModal';
import ProjectMapOverlay from './ProjectMapOverlay';


// Get language details from isoLanguages
const getLanguageDetails = (code) => {
    return isoLanguages.find((lang) => lang.code.toLowerCase() === code.toLowerCase()) || { name: code, countryCode: 'UN' };
};

const isLanguageRTL = (code) => {
    const details = getLanguageDetails(code);
    return details.direction === 'rtl';
};


const TranslateProject = () => {
    const { id } = useParams();
    const [originalSections, setOriginalSections] = useState([]);
    const [filteredOriginalSections, setFilteredOriginalSections] = useState([]);
    const [filteredTranslatedSections, setFilteredTranslatedSections] = useState([]);
    const [translatedSections, setTranslatedSections] = useState([]);
    const [approvedSections, setApprovedSections] = useState([]);
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('');
    const [overallProgress, setOverallProgress] = useState(0);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [translationService, setTranslationService] = useState('');
    const [tone, setTone] = useState('default');

    const sourceDirection = isLanguageRTL(sourceLanguage) ? 'rtl' : 'ltr';
    const targetDirection = isLanguageRTL(targetLanguage) ? 'rtl' : 'ltr';
    const [teamMembers, setTeamMembers] = useState([]);
    const [filterNumbers] = useState(false);
    const [filterSymbols] = useState(false);
    const [filterNumbersAndSymbols] = useState(false);
    const [projectCreator, setProjectCreator] = useState('');

    const token = useSelector((state) => state.auth.token);
    const [userPermissions, setUserPermissions] = useState({ 
        canUseMT: true, 
        canEditTM: true, 
        canEditGlossary: true,
        mtQuota: 1000000
    });
    const [userRole, setUserRole] = useState('independent');
    const [mtUsage, setMtUsage] = useState(0);
    const [mtQuota, setMtQuota] = useState(1000000);

    const [useTranslationMemory, setUseTranslationMemory] = useState(true);  // State for translation memory toggle
    useEffect(() => {
        if (userPermissions && !userPermissions.canEditTM) {
            setUseTranslationMemory(false);
        }
    }, [userPermissions]);
    const [glossaries, setGlossaries] = useState([]);
    const [selectedGlossaryId, setSelectedGlossaryId] = useState(null);
    const [selectedGlossaryTerms, setSelectedGlossaryTerms] = useState([]);
    const [filteredGlossaries, setFilteredGlossaries] = useState([]);
    const [saveProgress, setSaveProgress] = useState(0); // Track save progress
    const [isSaving, setIsSaving] = useState(false); // Track save status
    const [selectedSectionAnnotations, setSelectedSectionAnnotations] = useState([]);
    const [isAnnotationPanelOpen, setIsAnnotationPanelOpen] = useState(false);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
    const [annotatedSections, setAnnotatedSections] = useState(new Set());
    const { id: projectId } = useParams(); // Rename 'id' to 'projectId' for clarity
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [copied, setCopied] = useState(false);
    const [preferredAlgorithm, setPreferredAlgorithm] = useState('exact');
    const [skipNumberSegments, setSkipNumberSegments] = useState(false);
    const [skipSymbolSegments, setSkipSymbolSegments] = useState(false);
    const [skipNumberSymbolSegments, setSkipNumberSymbolSegments] = useState(false);
    const [confidenceScores, setConfidenceScores] = useState([]);
    const [openGlossaryDialog, setOpenGlossaryDialog] = useState(false);

    const [translationSources, setTranslationSources] = useState([]); // Track translation sources
    const [newGlossaryTerm, setNewGlossaryTerm] = useState({
        source: '',
        target: '',
        context: '',
        ignore: false,
    });
    const [sectionLinks, setSectionLinks] = useState([]);
    const [tagMaps, setTagMaps] = useState([]);
    const [grammarCheckResults, setGrammarCheckResults] = useState({});
    const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);
    const [grammarAnchorEl, setGrammarAnchorEl] = useState(null);
    const [activeGrammar, setActiveGrammar] = useState({ section: null, match: null });
    const [focusedSectionIndex, setFocusedSectionIndex] = useState(null);
    const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(true);
    const [suggestionData, setSuggestionData] = useState({
        memory: [],
        glossary: [],
        ai: null,
        history: []
    });
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const suggestionCache = useRef({}); // index -> { ai: string, memory: [], ... }
    const navigate = useNavigate();
    const location = useLocation();
    const [projectName, setProjectName] = useState('');
    const [projectFile, setProjectFile] = useState({ type: 'file', name: '' });

    const [filterEmptyTranslations, setFilterEmptyTranslations] = useState(false);
    const [sectionFilter, setSectionFilter] = useState('all');
    const [versionHistory, setVersionHistory] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [pageFilter, setPageFilter] = useState('all');

    const availablePages = useMemo(() => {
        const pages = [...new Set(tagMaps.map(tm => tm?._pageContext).filter(Boolean))];
        return pages.sort((a, b) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
        });
    }, [tagMaps]);
    
    // Context Sync State
    const [contextSyncMatches, setContextSyncMatches] = useState([]);
    const [showContextSyncAlert, setShowContextSyncAlert] = useState(false);
    const [showContextSyncModal, setShowContextSyncModal] = useState(false);
    const [contextSyncSourceIndex, setContextSyncSourceIndex] = useState(null);
    const [contextSyncNewTranslation, setContextSyncNewTranslation] = useState('');
    const contextSyncTimeoutRef = useRef(null);
    const segmentSaveTimeoutRef = useRef(null); // Ref for debouncing segment-level saves

    const [showMap, setShowMap] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    // Wrapper for version loading to match header prop expectation
    const handleVersionChange = (e) => {
        const idx = e.target.value;
        setSelectedVersion(idx);
        handleLoadVersion(idx);
    };
    const queryParams = new URLSearchParams(location.search);
    const initialDocVersion = parseInt(queryParams.get('docVersion'), 10);



    const getFileExtension = (name = '') =>
        name.includes('.') ? name.split('.').pop().toLowerCase() : '';

    // inline renderFileIcon (same logic as FileManager)
    const renderFileIcon = ({ type, name }) => {
        if (type === 'folder') {
            return <FolderIcon color="primary" />;
        }
        const ext = getFileExtension(name);
        if (defaultStyles[ext]) {
            return (
                <Box sx={{ width: 32 }}>
                    <FileIcon extension={ext} {...defaultStyles[ext]} />
                </Box>
            );
        }
        return (
            <Box sx={{ width: 32 }}>
                <FileIcon extension={ext || 'file'} />
            </Box>
        );
    };

    // Helper to select the right LanguageTool longCode:
    const getLanguageToolLocale = (src) => {
        if (!src) return 'en-GB';
        const norm = src.toLowerCase();
        // 1) exact match on longCode
        let entry = languageToolLocales.find(l => l.longCode.toLowerCase() === norm);
        if (entry) return entry.longCode;
        // 2) match start (e.g. “en” → first “en-”)
        entry = languageToolLocales.find(l => l.longCode.toLowerCase().startsWith(norm));
        if (entry) return entry.longCode;
        // 3) fallback to matching code only
        entry = languageToolLocales.find(l => l.code.toLowerCase() === norm.split(/[-_]/)[0]);
        return entry ? entry.longCode : 'en-GB';
    };

    let currentUserId;
    if (token) {
        const decodedToken = jwtDecode(token);
        currentUserId = decodedToken.id;
    }

    const user = useSelector((state) => state.auth.user) || {};
    const accountType = (user.accountType || 'essential').toLowerCase();

    const [docVersion, setDocVersion] = useState(
        isNaN(initialDocVersion) ? 0 : initialDocVersion
    );
    const getDraftKey = useCallback(
        (docVer = docVersion) => `translationDraft_${id}_${docVer}`,
        [id, docVersion]
    );
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    const saveDraft = useCallback(
        (overrideData) => {
            const data = {
                translatedSections,
                approvedSections,
                confidenceScores,
                updatedAt: Date.now(),
                ...overrideData,
            };
            try {
                localStorage.setItem(getDraftKey(), JSON.stringify(data));
            } catch (err) {
                console.error('Failed to save draft', err);
            }
        },
        [getDraftKey, translatedSections, approvedSections, confidenceScores]
    );

    const loadDraft = useCallback(
        (serverUpdatedAt, key) => {
            const draftKey = key || getDraftKey();
            const saved = localStorage.getItem(draftKey);
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    const draftTime = data.updatedAt || 0;
                    if (!serverUpdatedAt || draftTime > serverUpdatedAt) {
                        if (Array.isArray(data.translatedSections)) {
                            setTranslatedSections(data.translatedSections);
                        }
                        if (Array.isArray(data.approvedSections)) {
                            setApprovedSections(data.approvedSections);
                        }
                        if (Array.isArray(data.confidenceScores)) {
                            setConfidenceScores(data.confidenceScores);
                        }
                        return true;
                    }
                } catch (err) {
                    console.error('Failed to load draft', err);
                }
            }
            return false;
        },
        [getDraftKey]
    );

    const getTagMapForSection = useCallback(
        (sectionIdx) => {
            if (sectionIdx == null) return {};
            const mapsArray = Array.isArray(tagMaps) ? tagMaps : [];
            const linksArray = Array.isArray(sectionLinks) ? sectionLinks : [];
            const masterIdx = linksArray[sectionIdx];

            if (typeof masterIdx === 'number' && mapsArray[masterIdx]) {
                return mapsArray[masterIdx] || {};
            }

            return mapsArray[sectionIdx] || {};
        },
        [tagMaps, sectionLinks]
    );

    useEffect(() => {
        const handleBeforeUnload = () => saveDraft();
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [saveDraft]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const [currentPage, setCurrentPage] = useState(1);
    const sectionsPerPage = 50;

    // Use a ref to track the latest state for the unmount save
    const lastStateRef = useRef({ translatedSections, approvedSections, confidenceScores });
    useEffect(() => {
        lastStateRef.current = { translatedSections, approvedSections, confidenceScores };
    }, [translatedSections, approvedSections, confidenceScores]);

    // Debounced auto-save to localStorage
    useEffect(() => {
        const timer = setTimeout(() => {
            saveDraft();
        }, 1500); // 1.5s debounce for localStorage
        return () => {
            clearTimeout(timer);
        };
    }, [saveDraft]);

    // Save on unmount (navigation)
    useEffect(() => {
        return () => {
            const data = {
                ...lastStateRef.current,
                updatedAt: Date.now(),
            };
            try {
                localStorage.setItem(getDraftKey(), JSON.stringify(data));
            } catch (err) {
                console.error('Failed to save draft on unmount', err);
            }
        };
    }, [getDraftKey]);

    const [currentSections, setCurrentSections] = useState([]);



    // Utility functions for filtering
    const isNumber = (text) => /^[\d\s]+$/.test(text);
    const isSymbol = (text) => /^[^a-zA-Z0-9]+$/.test(text);
    const isNumberOrSymbol = (text) => /^[\d\s\W]+$/.test(text);

    const isProjectOwner = (typeof projectCreator === 'object'
        ? projectCreator._id
        : projectCreator?.toString()) === currentUserId?.toString();

    const isProjectManager = teamMembers.some(
        (m) =>
            m.userId?._id?.toString() === currentUserId?.toString() &&
            ['manager', 'lead'].includes(m.role)
    );

    const canCheckGrammar = (isProjectOwner || isProjectManager) &&
        ['growth', 'scale'].includes(accountType);



    // Fetch user's preferred algorithm when the component mounts
    useEffect(() => {
        const fetchUserSettings = async () => {
            try {
                const response = await axios.get('/api/user-settings', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPreferredAlgorithm(response.data.preferredAlgorithm || 'exact'); // Fallback to 'exact'
                setSkipNumberSegments(response.data.preTranslateSettings?.skipNumbers || false);
                setSkipSymbolSegments(response.data.preTranslateSettings?.skipSymbols || false);
                setSkipNumberSymbolSegments(response.data.preTranslateSettings?.skipNumbersAndSymbols || false);
            } catch (error) {
                console.error('Error fetching user settings:', error);
            }
        };

        fetchUserSettings();

        const fetchPermissions = async () => {
            try {
                const res = await axios.get('/api/team/my-permissions', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.permissions) {
                    setUserPermissions(res.data.permissions);
                    setUserRole(res.data.role);
                    setMtUsage(res.data.mtUsage || 0);
                    setMtQuota(res.data.mtQuota || 0);
                }
            } catch (err) {
                console.error("Error fetching team permissions:", err);
            }
        };
        fetchPermissions();
    }, [token]);

    useEffect(() => {
        const fetchAnnotations = async () => {
            try {
                const response = await axios.get(`/api/annotations/${projectId}/annotations`, {
                    headers: {
                        Authorization: `Bearer ${token}`, // Include token in the headers
                    },
                });
                const annotations = response.data;

                // Create a set of section indices that have annotations
                const annotatedIndices = new Set(annotations.map(annotation => annotation.sectionIndex));
                setAnnotatedSections(annotatedIndices);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized access - please check your credentials.');
                } else {
                    console.error('Error fetching annotations:', error);
                }
            }
        };

        if (projectId) {
            fetchAnnotations();
        }
    }, [projectId, token]);

    // Handler to add a section to annotatedSections
    const handleAnnotationAdded = (sectionIndex) => {
        setAnnotatedSections((prev) => new Set(prev).add(sectionIndex));
    };

    // Inside your TranslateProject component:

    // 1) Updated filter function (no longer resets page)
    const applyFilters = useCallback(() => {
        const filteredOriginal = [];
        const filteredTranslated = [];

        originalSections.forEach((section, index) => {
            const translation = translatedSections[index] || '';
            const hasErrors = (grammarCheckResults[index] || []).length > 0;
            const hasComment = annotatedSections.has(index);

            // first, the existing number/symbol/empty filters
            if (filterNumbers && isNumber(section)) return;
            if (filterSymbols && isSymbol(section)) return;
            if (filterNumbersAndSymbols && isNumberOrSymbol(section)) return;
            if (filterEmptyTranslations && translation.trim() !== '') return;

            // **new** filterMode logic:
            if (sectionFilter === 'untranslated' && translation.trim() !== '') return;
            if (sectionFilter === 'spelling' && !hasErrors) return;
            if (sectionFilter === 'comments' && !hasComment) return;

            if (pageFilter !== 'all') {
                const tm = getTagMapForSection(index);
                if (tm?._pageContext !== pageFilter) return;
            }

            filteredOriginal.push(section);
            filteredTranslated.push(translation);
        });

        setFilteredOriginalSections(filteredOriginal);
        setFilteredTranslatedSections(filteredTranslated);
    }, [
        filterNumbers, filterSymbols, filterNumbersAndSymbols, filterEmptyTranslations,
        sectionFilter, pageFilter,
        originalSections, translatedSections, grammarCheckResults, annotatedSections,
    ]);


    // 2) Reset to page 1 only when filter settings change
    useEffect(() => {
        applyFilters();
        setCurrentPage(1);
    }, [
        filterNumbers,
        filterSymbols,
        filterNumbersAndSymbols,
        filterEmptyTranslations,
        sectionFilter,
        pageFilter,
    ]);




    const handleAddGlossaryTerm = (index) => {
        // Ensure a glossary is selected
        if (!selectedGlossaryId) {
            alert('Please select a glossary before adding a term.');
            return;
        }

        const originalText = originalSections[index] || '';
        const translatedText = translatedSections[index] || '';

        if (!userPermissions.canEditGlossary) {
            setSnackbar({ open: true, message: 'Your team role does not allow editing glossaries.', severity: 'warning' });
            return;
        }

        // Determine if the user is the project owner
        const isProjectOwner = (typeof projectCreator === 'object'
            ? projectCreator._id
            : projectCreator?.toString()) === currentUserId?.toString();

        // Set up the new glossary term but do not submit yet
        const term = {
            source: originalText,
            target: translatedText,
            context: '',
            ignore: false,
            approved: isProjectOwner, // Set approved to false if not the project owner
        };

        // Open dialog with pre-filled term
        setNewGlossaryTerm(term);
        setOpenGlossaryDialog(true);
    };

    const handleCloseGlossaryDialog = () => {
        setOpenGlossaryDialog(false);
        setNewGlossaryTerm({ source: '', target: '', context: '', ignore: false });
    };

    const handleSubmitGlossaryTerm = async () => {
        if (!newGlossaryTerm.source) {
            alert('Source term is required!');
            return;
        }

        try {
            const response = await axios.post(`/api/glossary/${selectedGlossaryId}/terms`, newGlossaryTerm, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 201) {
                alert('Glossary term added successfully!');
                // Optionally update local glossary terms
                setSelectedGlossaryTerms((prevTerms) => [...prevTerms, response.data]);
            }
        } catch (error) {
            console.error('Error adding glossary term:', error);
            alert('Failed to add glossary term.');
        } finally {
            handleCloseGlossaryDialog();
        }
    };


    // Update displayed sections based on currentPage and filtered data
    useEffect(() => {
        const startIndex = (currentPage - 1) * sectionsPerPage;
        const endIndex = startIndex + sectionsPerPage;

        const filtersActive =
            filterEmptyTranslations ||
            filterNumbers ||
            filterSymbols ||
            filterNumbersAndSymbols ||
            sectionFilter !== 'all';

        const sections = filtersActive ? filteredOriginalSections : originalSections;
        const translations = filtersActive ? filteredTranslatedSections : translatedSections;

        // Update displayed sections
        setCurrentSections(
            sections.slice(startIndex, endIndex).map((section, index) => ({
                original: section,
                translated: translations[startIndex + index] || '',
            }))
        );
    }, [
        currentPage,
        filteredOriginalSections,
        filteredTranslatedSections,
        originalSections,
        translatedSections,
        sectionsPerPage,
        filterEmptyTranslations,
        filterNumbers,
        filterSymbols,
        filterNumbersAndSymbols,
        sectionFilter,
    ]);





    // Updated function to highlight and ignore glossary terms based on ignore status
    const highlightGlossaryTerms = (text, terms) => {
        if (typeof text !== 'string') return '';

        const glossaryWords = terms.map(term => ({
            words: term.source.split(/\s+/),
            ignore: term.ignore
        }));
        const words = text.split(/\s+/);
        const highlightedWords = [];
        let i = 0;

        while (i < words.length) {
            let foundMatch = false;

            for (const { words: termWords, ignore } of glossaryWords) {
                if (words[i] === termWords[0]) { // Case-sensitive match
                    let isMatch = true;

                    for (let j = 0; j < termWords.length; j++) {
                        if (words[i + j] !== termWords[j]) {
                            isMatch = false;
                            break;
                        }
                    }

                    if (isMatch) {
                        const termText = termWords.join(' ');

                        highlightedWords.push(
                            `<span title="Glossary Term" style="background-color: #FFFACD; border-radius: 4px; padding: 2px 4px; margin: 0 2px;">${ignore ? `<x>${termText}</x>` : termText
                            }</span>`
                        );

                        i += termWords.length;
                        foundMatch = true;
                        break;
                    }
                }
            }

            if (!foundMatch) {
                highlightedWords.push(words[i]);
                i++;
            }
        }

        return highlightedWords.join(' ');
    };

    const escapeHtml = (unsafe) =>
        unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

    const convertToHtml = (text, map) =>
        text
            .replace(/<\/(\d+)>/g, (_, n) => buildTagHtml(n, false, map))
            .replace(/<(\d+)>/g, (_, n) => buildTagHtml(n, true, map));

    const highlightGrammarIssues = (sectionIdx, text, issues = []) => {
        if (typeof text !== 'string') return '';
        const map = getTagMapForSection(sectionIdx);
        if (!issues.length) return convertToHtml(text, map);

        const sorted = [...issues].sort((a, b) => a.offset - b.offset);
        let result = '';
        let last = 0;

        sorted.forEach(({ offset, length, message }, idx) => {
            result += convertToHtml(text.slice(last, offset), map);
            const problematic = text.substr(offset, length);
            result += `<span class="grammar-error" data-section="${sectionIdx}" data-match="${idx}" title="${escapeHtml(message)}">${convertToHtml(problematic, map)}</span>`;
            last = offset + length;
        });

        result += convertToHtml(text.slice(last), map);
        return result;
    };

    const handleGrammarClick = (e) => {
        const target = e.target;
        if (target.classList.contains('grammar-error')) {
            const section = parseInt(target.getAttribute('data-section'), 10);
            const match = parseInt(target.getAttribute('data-match'), 10);
            setActiveGrammar({ section, match });
            setGrammarAnchorEl(target);
        }
    };

    const applyReplacement = (replacement) => {
        const { section, match } = activeGrammar;
        if (section == null || match == null) return;
        const matches = grammarCheckResults[section] || [];
        const m = matches[match];
        if (!m) return;
        const text = originalSections[section] || '';
        const before = text.slice(0, m.offset);
        const after = text.slice(m.offset + m.length);
        const newText = before + replacement + after;
        const updated = [...originalSections];
        updated[section] = newText;
        setOriginalSections(updated);
        setFilteredOriginalSections(updated);
        setGrammarAnchorEl(null);
    };

    // Apply filters only once on initial render or whenever filter dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const calculateCounts = useCallback((sections) => {
        let totalWords = 0;
        let totalChars = 0;
        sections.forEach((section) => {
            const words = section.split(/\s+/).filter(Boolean).length;
            totalWords += words;
            totalChars += section.length;
        });
        setWordCount(totalWords);
        setCharCount(totalChars);
    }, []);

    const calculateOverallProgress = useCallback((approvedSections) => {
        const totalSections = originalSections.length;
        const approvedCount = approvedSections.length;
        const newProgress = totalSections > 0 ? (approvedCount / totalSections) * 100 : 0;
        setOverallProgress(newProgress);
    }, [originalSections.length]);



    useEffect(() => {
        const fetchGlossaries = async () => {
            try {
                const response = await axios.get('/api/glossary', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setGlossaries(response.data);
                console.log("Fetched glossaries:", response.data);
            } catch (error) {
                console.error("Failed to load glossaries:", error);
            }
        };
        fetchGlossaries();
    }, [token]);

    // Glossary selection and placeholder replacement functions
    const handleGlossarySelection = async (glossaryId, persist = true) => {
        setSelectedGlossaryId(glossaryId);
        if (glossaryId) {
            try {
                const response = await axios.get(`/api/glossary/${glossaryId}/terms`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSelectedGlossaryTerms(response.data);
            } catch (error) {
                console.error("Failed to load glossary terms:", error);
            }
        } else {
            setSelectedGlossaryTerms([]);
        }

        if (persist) {
            try {
                await axios.put(
                    `/api/projects/${id}`,
                    { glossaryId: glossaryId || null },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (err) {
                console.error('Error updating project glossary:', err);
            }
        }
    };


    let ignoredTerms = []; // Array to store terms that are ignored

    const createPlaceholders = (text) => {
        let modifiedText = text;
        ignoredTerms = []; // Reset ignored terms for each translation

        selectedGlossaryTerms.forEach((term) => {
            if (term.ignore) {
                const regex = new RegExp(`\\b${term.source}\\b`, 'gi');
                if (regex.test(modifiedText)) {
                    ignoredTerms.push(term.source); // Log the term for later
                    modifiedText = modifiedText.replace(regex, '<x></x>'); // Replace with generic tag
                }
            }
        });

        return modifiedText;
    };

    const replacePlaceholdersWithTerms = (text) => {
        let modifiedText = text;
        let termIndex = 0; // Index to track terms in ignoredTerms array

        modifiedText = modifiedText.replace(/<x><\/x>/g, () => {
            // Replace each placeholder with the corresponding ignored term
            const term = ignoredTerms[termIndex];
            termIndex += 1; // Move to the next term for the next placeholder
            return term;
        });

        return modifiedText;
    };


    // Filter glossaries based on project languages
    useEffect(() => {
        if (glossaries.length > 0 && sourceLanguage && targetLanguage) {
            const matchingGlossaries = glossaries.filter(
                glossary =>
                    glossary.sourceLanguage.toLowerCase() === sourceLanguage.toLowerCase() &&
                    glossary.targetLanguage.toLowerCase() === targetLanguage.toLowerCase()
            );

            setFilteredGlossaries(matchingGlossaries);
        }
    }, [glossaries, sourceLanguage, targetLanguage]);



    useEffect(() => {
        const fetchTeamMembers = async () => {
            try {
                const response = await axios.get('/api/team', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setTeamMembers(response.data);
            } catch (err) {
                console.error('Error fetching team members:', err);
            }
        };
        fetchTeamMembers();
    }, [token]);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const url = !isNaN(initialDocVersion)
                    ? `/api/projects/${id}?docVersion=${initialDocVersion}`
                    : `/api/projects/${id}`;
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const { project } = response.data;
                setOriginalSections(project.sections || []);
                setTranslatedSections(project.translatedSections || []);
                setConfidenceScores(project.confidenceScores || {});
                setTranslationSources(project.translationSources || new Array((project.sections || []).length).fill(''));
                setFilteredOriginalSections(project.sections || []);
                setFilteredTranslatedSections(project.translatedSections || []);
                setSourceLanguage(project.sourceLanguage);
                setTargetLanguage(project.targetLanguage);
                setProjectCreator(project.createdBy);
                setApprovedSections(project.approvedSections || []);
                setSectionLinks(project.sectionLinks || []); // <-- NEW
                setTagMaps(project.tagMaps || []);
                setTranslationService(project.translationService || 'deepl');
                setTone(project.tone || 'default');
                if (project.glossaryId) {
                    await handleGlossarySelection(project.glossaryId, false);
                }
                calculateCounts(project.sections || []);
                calculateOverallProgress(project.approvedSections || []);
                setProjectName(project.projectName);

                // derive a filename from fileUrl or templateFilePath:
                const path = project.fileUrl || project.templateFilePath || '';
                const filename = path.split('/').pop() || project.projectName;
                setProjectFile({ type: 'file', name: filename });

                const docVer = !isNaN(initialDocVersion)
                    ? initialDocVersion
                    : project.currentVersion || 0;
                setDocVersion(docVer);
                const draftKey = `translationDraft_${id}_${docVer}`;
                const history =
                    (project.documentVersions || [])[docVer]?.translationVersions || [];
                setVersionHistory(history);

                const latestServerUpdatedAt = new Date(
                    history[history.length - 1]?.updatedAt || project.updatedAt || 0
                ).getTime();

                const usedDraft = loadDraft(latestServerUpdatedAt, draftKey);
                if (!usedDraft) {
                    localStorage.removeItem(draftKey);
                }
            } catch (err) {
                console.error('Error fetching project:', err);
                loadDraft();
            }
        };

        fetchProject();
    }, [id, token, initialDocVersion, calculateCounts, calculateOverallProgress, loadDraft]);




    // Fetch annotations for a specific section
    const fetchAnnotations = async (sectionIndex) => {
        try {
            const response = await axios.get(`/api/annotations/${id}/annotations/${sectionIndex}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedSectionAnnotations(response.data);
        } catch (error) {
            console.error('Error fetching annotations:', error);
        }
    };

    // Handle opening annotation panel for a section
    const handleAnnotationOpen = (index) => {
        setSelectedSectionIndex(index);
        setIsAnnotationPanelOpen(true);
        fetchAnnotations(index);
    };

    // Handle closing the annotation panel
    const handleAnnotationClose = () => {
        setIsAnnotationPanelOpen(false);
        setSelectedSectionAnnotations([]);
    };





    const toggleApprovalForSections = (toggleAll) => {
        const sectionsToToggle = toggleAll ? filteredIndices : currentSections;
        const updatedApprovedSections = new Set(approvedSections);

        sectionsToToggle.forEach((section, index) => {
            const globalIndex = toggleAll ? index : index + indexOfFirstSection;

            if (updatedApprovedSections.has(globalIndex)) {
                updatedApprovedSections.delete(globalIndex);  // Unapprove if already approved
            } else {
                updatedApprovedSections.add(globalIndex);  // Approve if not already approved
            }
        });

        setApprovedSections(Array.from(updatedApprovedSections));
        calculateOverallProgress(Array.from(updatedApprovedSections));
    };

    const toggleApproval = async (index) => {
        const globalIndex = index + indexOfFirstSection;
        const isApproved = approvedSections.includes(globalIndex);
        const updatedApproved = isApproved
            ? approvedSections.filter((i) => i !== globalIndex)
            : [...approvedSections, globalIndex];

        setApprovedSections(updatedApproved);
        calculateOverallProgress(updatedApproved);


        try {
            await axios.put(`/api/projects/${id}/segment`, {
                index: globalIndex,
                approved: !isApproved,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (err) {
            console.error('Error saving toggle state:', err);
        }
    };

    const handleSaveTranslation = async () => {
        setIsSaving(true); // Start showing the progress indicator
        setSaveProgress(0); // Reset progress to 0 at the start

        try {
            // Ensure the arrays have the correct length matching `originalSections`
            const totalSections = originalSections.length;

            // Ensure translatedSections and approvedSections have correct lengths
            const formattedTranslatedSections = Array.from({ length: totalSections }, (_, index) =>
                translatedSections[index] || ''
            );

            const formattedApprovedSections = approvedSections.filter(
                (index) => index < totalSections
            );

            // Ensure confidenceScores is an array of the correct length
            const formattedConfidenceScores = Array.from({ length: totalSections }, (_, index) =>
                confidenceScores[index] || 0
            );

            // Log the payload before sending for debugging
            console.log({
                translatedSections: formattedTranslatedSections,
                approvedSections: formattedApprovedSections,
                confidenceScores: formattedConfidenceScores,
                sections: isProjectOwner ? originalSections : undefined,
            });

            // Send data to the backend with version creation flag
            await axios.put(
                `/api/projects/${id}/translate`,
                {
                    translatedSections: formattedTranslatedSections,
                    approvedSections: formattedApprovedSections,
                    confidenceScores: formattedConfidenceScores,
                    sections: isProjectOwner ? originalSections : undefined,
                    createVersion: true, // Explicitly create a version history entry
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const { data } = await axios.get(`/api/projects/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const proj = data.project;
            const docVer = proj.currentVersion || 0;
            setDocVersion(docVer);
            const history =
                (proj.documentVersions || [])[docVer]?.translationVersions || [];
            setVersionHistory(history);
            setSelectedVersion('');

            // clear saved drafts after successful save
            clearTranslationDrafts();

            alert('Translation saved successfully!');
        } catch (err) {
            console.error('Error saving translation:', err);
            alert('Error saving translation.');
        } finally {
            setIsSaving(false); // Hide the progress indicator when done
            setSaveProgress(0); // Reset progress when saving is complete
        }
    };

    const handleApproveAll = async () => {
        try {
            const { data } = await axios.post(
                `/api/projects/${id}/approve-all`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Update local state so the UI reflects the approvals immediately
            const updatedApproved = data.approvedIndices || [];
            const merged = Array.from(new Set([...approvedSections, ...updatedApproved]));
            setApprovedSections(merged);
            calculateOverallProgress(merged);
            setSnackbar({
                open: true,
                message: `${data.approved} segment${data.approved !== 1 ? 's' : ''} approved and saved to Translation Memory.`,
                severity: 'success',
            });
        } catch (err) {
            console.error('Error approving all segments:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.msg || 'Failed to approve all segments.',
                severity: 'error',
            });
        }
    };

    const handleLoadVersion = async (index) => {
        try {
            const params = [];
            if (!isNaN(docVersion)) params.push(`docVersion=${docVersion}`);
            if (index !== '' && index !== null && index !== undefined)
                params.push(`versionIndex=${index}`);
            const url = `/api/projects/${id}` + (params.length ? `?${params.join('&')}` : '');
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const { project } = response.data;
            setOriginalSections(project.sections || []);
            setTranslatedSections(project.translatedSections || []);
            setConfidenceScores(project.confidenceScores || []);
            setTranslationSources(project.translationSources || []);
            setApprovedSections(project.approvedSections || []);
            setSectionLinks(project.sectionLinks || []);
            setTagMaps(project.tagMaps || []);
            calculateCounts(project.sections || []);
            calculateOverallProgress(project.approvedSections || []);
            setProjectName(project.projectName);
            const path = project.fileUrl || project.templateFilePath || '';
            const filename = path.split('/').pop() || project.projectName;
            setProjectFile({ type: 'file', name: filename });

            const history =
                (project.documentVersions || [])[docVersion]?.translationVersions || [];
            setVersionHistory(history);
            clearTranslationDrafts();
        } catch (err) {
            console.error('Error loading version:', err);
            alert('Failed to load version');
        }
    };








    const handleTranslate = async (index) => {
        const originalText = originalSections[index + indexOfFirstSection];

        if (!translationService) {
            alert('Please select a translation service.');
            return;
        }

        if (!userPermissions.canUseMT) {
            setSnackbar({ open: true, message: 'Your team role does not allow using Machine Translation.', severity: 'warning' });
            return;
        }

        // Check if the originalText matches a glossary term with ignore status
        const exactGlossaryTerm = selectedGlossaryTerms.find(
            (term) => term.source === originalText && term.ignore
        );

        if (exactGlossaryTerm) {
            // Use glossary source term with <x> tags if marked as ignored
            const taggedText = `<x>${exactGlossaryTerm.source}</x>`;
            handleTranslationChange(index, taggedText);
            console.log(`Translation source: ignored glossary term | Original Text: "${originalText}" | Tagged Text: "${taggedText}"`);
            return;
        }

        // Proceed with creating placeholders if a glossary is selected
        const textWithPlaceholders = selectedGlossaryId ? createPlaceholders(originalText) : originalText;

        // Ensure source language is set to 'en' if it's 'en-gb'
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        try {
            let translatedText = '';
            let confidence = 0;
            let source = '';

            if (useTranslationMemory) {
                try {
                    const mem = await axios.post(
                        '/api/translation-memory/check',
                        { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                        {
                            params: { algorithm: preferredAlgorithm },
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    if (mem.data.translatedText) {
                        translatedText = mem.data.translatedText;
                        confidence = mem.data.confidenceScore || 0;
                        source = 'memory';
                    }
                } catch {
                    // continue to AI
                }
            }

            if (!translatedText) {
                const endpoint = translationService === 'openai'
                    ? '/api/openai/translate'
                    : '/api/translate';
                const response = await axios.post(
                    endpoint,
                    {
                        text: textWithPlaceholders,
                        sourceLanguage: srcLang,
                        targetLanguage,
                        service: translationService,
                        tone,
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                let apiTranslatedText = response.data.translatedText;
                selectedGlossaryTerms.forEach((term) => {
                    if (term.ignore) {
                        const tagPattern = new RegExp(`<x>${term.source}</x>`, 'gi');
                        apiTranslatedText = apiTranslatedText.replace(tagPattern, term.source);
                    }
                });

                const finalTranslatedText = selectedGlossaryId
                    ? replacePlaceholdersWithTerms(apiTranslatedText)
                    : apiTranslatedText;

                translatedText = finalTranslatedText;
                confidence = 100;
                source = 'ai';
            }

            handleTranslationChange(index, translatedText, confidence, source);

            console.log(`Translation source: ${source === 'memory' ? 'Memory' : 'AI'} | Original Text: "${originalText}" | Translated Text: "${translatedText}"`);

        } catch (err) {
            console.error('Error translating text:', err);
            const errorMsg = err.response?.data?.msg || 'Error translating text.';
            if (errorMsg.toLowerCase().includes('limit reached') || errorMsg.toLowerCase().includes('quota reached')) {
                setSnackbar({ 
                    open: true, 
                    message: 'MT Quota reached for your team role. Please contact your team manager.', 
                    severity: 'error' 
                });
            } else {
                setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            }
        }
    };

    const handleTranslationChange = async (index, value, confidence = 100, source = null) => {
        const globalIndex = index + indexOfFirstSection;
        const originalText = originalSections[globalIndex];
        const translatedText = value;
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        // Update the translated sections with the new value
        const updatedTranslations = [...translatedSections];
        updatedTranslations[globalIndex] = translatedText;

        // Apply glossary highlights to the translated text
        const highlightedText = highlightGlossaryTerms(translatedText, selectedGlossaryTerms);
        updatedTranslations[globalIndex] = highlightedText;
        setTranslatedSections(updatedTranslations);

        const updatedSources = [...translationSources];
        if (source) {
            updatedSources[globalIndex] = source;
        }
        setTranslationSources(updatedSources);

        // Update the confidence score for the current section
        setConfidenceScores((prevScores) => ({
            ...prevScores,
            [globalIndex]: confidence,
        }));

        // Save the updated translation and confidence score in the backend efficiently
        try {
            await axios.put(`/api/projects/${id}/segment`, {
                index: globalIndex,
                translatedText: translatedText,
                confidence: confidence,
                source: source
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log(`Translation and confidence score saved for section ${globalIndex}`);
        } catch (err) {
            console.error('Error saving translation and confidence score:', err);
        }

    };

    const fetchSuggestionsForSegment = useCallback(async (index, revealAI = false) => {
        if (index === null || index === undefined) return;

        const originalText = originalSections[index];
        const srcLang = sourceLanguage === 'en-gb' ? 'en' : sourceLanguage;

        // Check cache first
        const cache = suggestionCache.current[index];

        // 1. Glossary Matches (Local calculation)
        const glossaryMatches = selectedGlossaryTerms.filter(term =>
            originalText.toLowerCase().includes(term.source.toLowerCase())
        );

        // 2. Project History (Local calculation)
        const historyMatches = [];
        const seenTargets = new Set();
        originalSections.forEach((text, i) => {
            if (i !== index && text === originalText && translatedSections[i] && !seenTargets.has(translatedSections[i])) {
                historyMatches.push({
                    source: text,
                    target: translatedSections[i]
                });
                seenTargets.add(translatedSections[i]);
            }
        });

        const newSuggestions = {
            glossary: glossaryMatches,
            history: historyMatches.slice(0, 5),
            ai: cache?.ai || (historyMatches[0]?.target) || null, // Reuse history if available
            memory: cache?.memory || []
        };

        setSuggestionData(newSuggestions);

        // 3. Memory Matches (Async) - cache if not already
        if (useTranslationMemory && (!cache || !cache.memory || cache.memory.length === 0)) {
            try {
                const memRes = await axios.post(
                    '/api/translation-memory/check',
                    { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage },
                    {
                        params: { algorithm: preferredAlgorithm },
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                if (memRes.data.translatedText) {
                    const memData = [{
                        translatedText: memRes.data.translatedText,
                        confidence: memRes.data.confidenceScore || 0
                    }];
                    setSuggestionData(prev => {
                        const updated = { ...prev, memory: memData };
                        suggestionCache.current[index] = { ...suggestionCache.current[index], memory: memData };
                        return updated;
                    });
                }
            } catch (err) {
                console.error('Error fetching TM for suggestions:', err);
            }
        }

        // 4. AI Suggestion (Async) - only if revealAI is true and not cached
        if (revealAI && !cache?.ai) {
            if (!userPermissions.canUseMT) {
                setSnackbar({ open: true, message: 'Your team role does not allow using Machine Translation.', severity: 'warning' });
                return;
            }
            setIsGeneratingAI(true);
            try {
                const endpoint = translationService === 'openai' ? '/api/openai/translate' : '/api/translate';
                const aiRes = await axios.post(
                    endpoint,
                    {
                        text: originalText,
                        sourceLanguage: srcLang,
                        targetLanguage,
                        service: translationService || 'deepl',
                        tone,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (aiRes.data.translatedText) {
                    const aiText = aiRes.data.translatedText;
                    setSuggestionData(prev => {
                        const updated = { ...prev, ai: aiText };
                        suggestionCache.current[index] = { ...suggestionCache.current[index], ai: aiText };
                        return updated;
                    });
                }
            } catch (err) {
                console.error('Error fetching AI suggestion:', err);
                const errorMsg = err.response?.data?.msg || '';
                if (errorMsg.toLowerCase().includes('limit reached') || errorMsg.toLowerCase().includes('quota reached')) {
                    setSnackbar({ 
                        open: true, 
                        message: 'MT Quota reached for your team role. Please contact your team manager.', 
                        severity: 'error' 
                    });
                }
            } finally {
                setIsGeneratingAI(false);
            }
        }
    }, [originalSections, translatedSections, selectedGlossaryTerms, sourceLanguage, targetLanguage, useTranslationMemory, currentUserId, preferredAlgorithm, token, translationService, tone]);

    const handleRowFocus = (index) => {
        if (focusedSectionIndex !== index) {
            setFocusedSectionIndex(index);
            fetchSuggestionsForSegment(index);
            if (!isSuggestionsPanelOpen) {
                setIsSuggestionsPanelOpen(true);
            }
        }
    };





    const handleGrammarCheck = async () => {
        setIsCheckingGrammar(true);
        try {
            const locale = getLanguageToolLocale(sourceLanguage);
            const res = await axios.post(
                '/api/grammar/check',
                { language: locale, sections: originalSections },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const keyed = {};
            (res.data.results || []).forEach(r => {
                const sorted = (r.matches || []).slice().sort((a, b) => a.offset - b.offset);
                keyed[r.index] = sorted;
            });
            setGrammarCheckResults(keyed);
        } catch (err) {
            console.error('Grammar check error:', err);
            setGrammarCheckResults({});
        } finally {
            setIsCheckingGrammar(false);
        }
    };

    const calculateTranslationProgress = useCallback(() => {
        const totalSections = originalSections.length;
        const translatedCount = translatedSections.filter(section => section).length;
        return totalSections > 0 ? (translatedCount / totalSections) * 100 : 0;
    }, [originalSections, translatedSections]);

    const translationProgress = calculateTranslationProgress();

    const handleCopyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true); // Show feedback
        });
    };

    // Always use the full number of segments from sectionLinks
    // --- Filtered Display Arrays and Pagination ---
    // Build a flat list of all section indices
    const allIndices = originalSections.map((_, i) => i);

    // Filter by dropdown selection
    let filteredIndices = allIndices;
    if (sectionFilter === "untranslated") {
        filteredIndices = filteredIndices.filter(i => !translatedSections[i]?.trim());
    } else if (sectionFilter === "errors") {
        filteredIndices = filteredIndices.filter(i => (grammarCheckResults[i] || []).length > 0);
    } else if (sectionFilter === "comments") {
        filteredIndices = filteredIndices.filter(i => annotatedSections.has(i));
    }

    if (pageFilter !== "all") {
        filteredIndices = filteredIndices.filter(i => {
            const tm = getTagMapForSection(i);
            return tm?._pageContext === pageFilter;
        });
    }

    // Paginate the filtered set
    const totalSections = filteredIndices.length;
    const indexOfLastSection = Math.min(currentPage * sectionsPerPage, totalSections);
    const indexOfFirstSection = (currentPage - 1) * sectionsPerPage;
    const pagedIndices = filteredIndices.slice(indexOfFirstSection, indexOfLastSection);

    const handleOriginalTextChange = (globalIndex, newText) => {
        setOriginalSections(prev => {
            const u = [...prev];
            u[globalIndex] = newText;
            return u;
        });
    };


    const handleLocalTranslationChange = async (globalIndex, newValue, confidence = null, source = null) => {
        setTranslatedSections(prev => {
            const u = [...prev];
            u[globalIndex] = newValue;
            return u;
        });

        setConfidenceScores(prev => ({
            ...prev,
            [globalIndex]: confidence
        }));

        if (source) {
            setTranslationSources(prev => {
                const u = [...prev];
                u[globalIndex] = source;
                return u;
            });
        }

        // --- Debounced Server Sync ---
        if (segmentSaveTimeoutRef.current) clearTimeout(segmentSaveTimeoutRef.current);
        
        segmentSaveTimeoutRef.current = setTimeout(async () => {
            try {
                await axios.put(`/api/projects/${id}/segment`, {
                    index: globalIndex,
                    translatedText: newValue,
                    confidence: confidence,
                    source: source
                }, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                console.log(`Segment ${globalIndex} synced to server (debounced)`);
            } catch (err) {
                console.error('Error saving segment change:', err);
            }
        }, 800); // 800ms debounce

        // --- Context Sync Logic ---
        if (contextSyncTimeoutRef.current) clearTimeout(contextSyncTimeoutRef.current);
        
        contextSyncTimeoutRef.current = setTimeout(() => {
            const matches = findSimilarSegments(originalSections[globalIndex], originalSections, globalIndex, translatedSections, newValue, 1.0);
            if (matches.length > 0) {
                setContextSyncMatches(matches);
                setContextSyncSourceIndex(globalIndex);
                setContextSyncNewTranslation(newValue);
                setShowContextSyncAlert(true);
            } else {
                setShowContextSyncAlert(false);
            }
        }, 1500); // Debounce for 1.5 seconds
    };

    const handleContextSyncReplaceAll = async () => {
        const segmentsToUpdate = contextSyncMatches.map(m => m.index);
        await handleContextSyncReplaceSelected(segmentsToUpdate);
        setShowContextSyncAlert(false);
    };

    const handleContextSyncReplaceSelected = async (indices) => {
        const updatedTranslations = [...translatedSections];
        const updatePromises = indices.map(async (idx) => {
            updatedTranslations[idx] = contextSyncNewTranslation;
            
            // Persist each change to backend
            return axios.put(`/api/projects/${id}/segment`, {
                index: idx,
                translatedText: contextSyncNewTranslation,
                confidence: 100,
                source: 'context-sync'
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });

        setTranslatedSections(updatedTranslations);
        try {
            await Promise.all(updatePromises);
            console.log(`Context Sync: Updated ${indices.length} segments`);
        } catch (err) {
            console.error('Error in Context Sync bulk update:', err);
        }
        setShowContextSyncAlert(false);
    };

    const handleGrammarReplace = (globalIndex, _sug, errIdx) => {
        setGrammarCheckResults(prev => {
            const next = { ...prev };
            const list = [...(next[globalIndex] || [])];
            list.splice(errIdx, 1);
            next[globalIndex] = list;
            return next;
        });
    };

    const handleMapSegmentClick = (index) => {
        const tm = getTagMapForSection(index);
        const page = tm._pageContext || 'all';
        
        // If filtering by page, we need to switch to that page or "all"
        if (pageFilter !== 'all' && pageFilter !== page) {
            setPageFilter(page);
        }

        const targetPage = Math.floor(index / sectionsPerPage) + 1;
        setCurrentPage(targetPage);
        setShowMap(false);

        // Scroll to it
        setTimeout(() => {
            const element = document.getElementById(`row-${index}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('highlight-flash');
                setTimeout(() => element.classList.remove('highlight-flash'), 2000);
            }
        }, 500);
    };



    return (
        <>
            {isOffline && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    You're offline. Changes will be saved locally.
                </Alert>
            )}

            {/* NEW HEADER COMPONENT */}
            <TranslationHeader
                projectName={projectName}
                sourceLanguage={sourceLanguage}
                targetLanguage={targetLanguage}
                sourceLangDetails={getLanguageDetails(sourceLanguage)}
                targetLangDetails={getLanguageDetails(targetLanguage)}
                wordCount={wordCount}
                charCount={charCount}
                overallProgress={overallProgress}
                isSaving={isSaving}
                isProjectOwner={(typeof projectCreator === 'object' ? projectCreator._id : projectCreator?.toString()) === currentUserId?.toString()}
                useTranslationMemory={useTranslationMemory}
                onToggleTranslationMemory={(e) => setUseTranslationMemory(e.target.checked)}
                onBack={() => {
                    saveDraft();
                    navigate(`/dashboard/projects/${id}`);
                }}
                onSave={handleSaveTranslation}
                onQA={handleGrammarCheck}
                onToggleMap={() => setShowMap(!showMap)}
                onApproveAll={handleApproveAll}
                filterMode={sectionFilter}
                setFilterMode={setSectionFilter}
                filterEmptyTranslations={filterEmptyTranslations}
                setFilterEmptyTranslations={setFilterEmptyTranslations}
                versionHistory={versionHistory}
                selectedVersion={selectedVersion}
                onChangeVersion={handleVersionChange}
                pageFilter={pageFilter}
                setPageFilter={setPageFilter}
                availablePages={availablePages}
                translationService={translationService}
                onServiceChange={(service) => {
                    setTranslationService(service);
                    axios.put(`/api/projects/${id}`, { translationService: service }, { headers: { Authorization: `Bearer ${token}` } })
                        .catch(err => console.error('Error updating service:', err));
                }}
            />

            <ProjectMapOverlay 
                isOpen={showMap} 
                onClose={() => setShowMap(false)} 
                sections={originalSections} 
                translatedSections={translatedSections}
                approvedSections={approvedSections}
                tagMaps={tagMaps} 
                onSegmentClick={handleMapSegmentClick}
                currentSegmentIndex={focusedSectionIndex}
            />

            <Box sx={{ bgcolor: '#f1f5f9', minHeight: '100vh', pb: 10 }}>
                <Container maxWidth="xl" sx={{ pt: 4 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={isSuggestionsPanelOpen ? 9 : 12}>
                            <Box sx={{ mb: 2 }}>
                                {pagedIndices.map((globalIndex, pageIdx) => {
                                    const originalText = originalSections[globalIndex];
                                    const translatedText = translatedSections[globalIndex] || "";
                                    const masterIdx = sectionLinks[globalIndex];
                                    const isRepeat = sectionLinks.filter(idx => idx === masterIdx).length > 1;
                                    const tagMap = getTagMapForSection(globalIndex);

                                    return (
                                        <TranslationRow
                                            key={globalIndex}
                                            id={`row-${globalIndex}`}
                                            globalIndex={globalIndex}
                                            originalText={originalText}
                                            translatedText={translatedText}
                                            isRepeat={isRepeat}
                                            tagMap={tagMap}
                                            isProjectOwner={isProjectOwner}
                                            sourceDirection={sourceDirection}
                                            targetDirection={targetDirection}
                                            grammarErrors={grammarCheckResults[globalIndex] || []}
                                            hasNote={annotatedSections.has(globalIndex)}
                                            isApproved={approvedSections.includes(globalIndex)}
                                            confidenceScore={confidenceScores[globalIndex]}
                                            translationSource={translationSources[globalIndex]}
                                            onOriginalTextChange={handleOriginalTextChange}
                                            onTranslationChange={handleLocalTranslationChange}
                                            onGrammarReplace={handleGrammarReplace}
                                            onGrammarClick={handleGrammarClick}
                                            highlightGrammarIssues={highlightGrammarIssues}
                                            onToggleApproval={() => toggleApproval(globalIndex - indexOfFirstSection)}
                                            onTranslate={() => handleTranslate(globalIndex - indexOfFirstSection)}
                                            onAddAnnotation={() => handleAnnotationOpen(globalIndex)}
                                            onCopyToClipboard={handleCopyToClipboard}
                                            onAddGlossaryTerm={() => handleAddGlossaryTerm(globalIndex)}
                                            onRowFocus={handleRowFocus}
                                        />
                                    );
                                })}
                            </Box>
                            
                            {/* Pagination */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                <Pagination
                                    count={Math.ceil(totalSections / sectionsPerPage)}
                                    page={currentPage}
                                    onChange={(_e, v) => setCurrentPage(v)}
                                    color="primary"
                                    size="large"
                                />
                            </Box>
                        </Grid>

                        {/* Suggestions Panel */}
                        {isSuggestionsPanelOpen && (
                            <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                                <SmartSuggestions
                                    isOpen={isSuggestionsPanelOpen}
                                    onClose={() => setIsSuggestionsPanelOpen(false)}
                                    focusedSegment={focusedSectionIndex}
                                    originalText={focusedSectionIndex !== null ? originalSections[focusedSectionIndex] : ''}
                                    suggestions={suggestionData}
                                    onInsert={(text, confidence, source) => handleLocalTranslationChange(focusedSectionIndex, text, confidence, source)}
                                    onRevealAI={() => fetchSuggestionsForSegment(focusedSectionIndex, true)}
                                    isGeneratingAI={isGeneratingAI}
                                    mtUsage={mtUsage}
                                    mtQuota={mtQuota}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Container>
            </Box>


            {/* Snackbar for Copy Feedback */}
            <Snackbar
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setCopied(false)} severity="success" sx={{ width: '100%' }}>
                    Text copied to clipboard!
                </Alert>
            </Snackbar>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>


            {/* Glossary Term Popup */}
            <Modal open={openGlossaryDialog} onClose={handleCloseGlossaryDialog}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Add Glossary Term</Typography>
                        <IconButton onClick={handleCloseGlossaryDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Source Term"
                        type="text"
                        fullWidth
                        value={newGlossaryTerm.source}
                        onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, source: e.target.value })}
                        sx={{ mb: 2 }}
                        inputProps={{ spellCheck: false, dir: sourceDirection, style: { textAlign: sourceDirection === 'rtl' ? 'right' : 'left' } }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newGlossaryTerm.ignore}
                                onChange={(e) =>
                                    setNewGlossaryTerm({
                                        ...newGlossaryTerm,
                                        ignore: e.target.checked,
                                        target: e.target.checked ? '' : newGlossaryTerm.target,
                                    })
                                }
                                color="primary"
                            />
                        }
                        label="Ignore Term"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Target Term"
                        type="text"
                        fullWidth
                        value={newGlossaryTerm.target}
                        onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, target: e.target.value })}
                        disabled={newGlossaryTerm.ignore}
                        sx={{ mb: 2 }}
                        inputProps={{ spellCheck: false, dir: targetDirection, style: { textAlign: targetDirection === 'rtl' ? 'right' : 'left' } }}
                    />
                    <TextField
                        margin="dense"
                        label="Context"
                        type="text"
                        fullWidth
                        value={newGlossaryTerm.context}
                        onChange={(e) => setNewGlossaryTerm({ ...newGlossaryTerm, context: e.target.value })}
                        placeholder="Optional usage information"
                        sx={{ mb: 2 }}
                        inputProps={{ spellCheck: false }}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSubmitGlossaryTerm}
                        disabled={!newGlossaryTerm.source.trim()}
                    >
                        Submit
                    </Button>
                </Box>
            </Modal>

            {/* Context Sync Components */}
            <ContextSyncAlert 
                isVisible={showContextSyncAlert}
                count={contextSyncMatches.length}
                onReplaceAll={handleContextSyncReplaceAll}
                onViewMatches={() => { setShowContextSyncModal(true); setShowContextSyncAlert(false); }}
                onIgnore={() => setShowContextSyncAlert(false)}
            />

            <ContextSyncModal
                open={showContextSyncModal}
                onClose={() => setShowContextSyncModal(false)}
                matches={contextSyncMatches}
                newTranslation={contextSyncNewTranslation}
                onReplaceSelected={handleContextSyncReplaceSelected}
            />

            {/* Grammar Suggestions Menu */}
            <Menu
                anchorEl={grammarAnchorEl}
                open={Boolean(grammarAnchorEl)}
                onClose={() => setGrammarAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {(() => {
                    const { section, match } = activeGrammar;
                    const m =
                        section != null && match != null && grammarCheckResults[section]
                            ? grammarCheckResults[section][match]
                            : null;
                    return m && m.replacements
                        ? m.replacements.slice(0, 5).map((r, i) => (
                            <MenuItem key={i} onClick={() => applyReplacement(r.value)}>
                                {r.value}
                            </MenuItem>
                        ))
                        : <MenuItem disabled>No suggestions</MenuItem>;
                })()}
            </Menu>

            {/* Annotation Panel */}
            <AnnotationPanel
                projectId={projectId}
                sectionIndex={selectedSectionIndex}
                open={isAnnotationPanelOpen}
                onClose={handleAnnotationClose}
                onAnnotationAdded={handleAnnotationAdded}
            />
        </>
    );
};

export default TranslateProject;

